import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for Google Analytics 4 (GA4) integration
 * 
 * Features:
 * - Automatic page view tracking on route changes
 * - Dynamic gtag.js script loading
 * - Production-only tracking to keep analytics clean
 * - Type-safe window.gtag implementation
 */
export function useGoogleAnalytics(): void {
  const location = useLocation();

  useEffect(() => {
    // Skip initialization in non-production environments to keep analytics data clean
    if (import.meta.env.MODE !== 'production') {
      console.log('[GA4] Skipping in development mode');
      return;
    }

    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    
    if (!measurementId) {
      console.warn('[GA4] VITE_GA_MEASUREMENT_ID not configured in .env');
      return;
    }

    // Initialize gtag.js if not already loaded
    if (!window.dataLayer) {
      console.log('[GA4] Initializing Google Analytics with ID:', measurementId);
      
      // Load gtag.js script dynamically
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]): void {
        window.dataLayer.push(args);
      }
      (window as any).gtag = gtag;
      
      // Configure GA4 with the measurement ID
      gtag('js', new Date());
      gtag('config', measurementId, {
        send_page_view: false // We'll manually send page views on route changes
      });
    }

    // Send page_view event on route changes
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
      
      console.log('[GA4] Page view tracked:', location.pathname);
    }
  }, [location]);
}